import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  doorTypeIds,
  getDoorCopy,
  parseDoorType,
  productsFeed,
  regions,
  type DoorTypeId,
} from '@planner/config';
import type { ProductDataFile } from '@planner/shared';
import { useAuth } from '../hooks/useAuth.js';
import { getProducts, triggerSync } from '../lib/api.js';
import { formatCurrency } from '../lib/format.js';

/** Raw `PS-SECBAR-DOORS_StockItems` row as the sync stores it. */
type RawRow = Record<string, unknown>;

function text(row: RawRow, key: string): string {
  const value = row[key];
  return value === undefined || value === null ? '' : String(value).trim();
}

/** Tab order: security first, then barrier. Every configured door type gets a tab. */
const tabTypes: readonly DoorTypeId[] = [
  'security',
  ...doorTypeIds.filter((id) => id !== 'security'),
];

/** Columns the search box matches against. */
const searchFields = ['inventoryID', 'description', 'bunningsItemNumber', 'frameColour'];

function matchesSearch(row: RawRow, term: string): boolean {
  if (!term) {
    return true;
  }
  const needle = term.toLowerCase();
  return searchFields.some((field) => text(row, field).toLowerCase().includes(needle));
}

function formatBand(row: RawRow, minKey: string, maxKey: string): string {
  const min = text(row, minKey);
  const max = text(row, maxKey);
  if (!min && !max) {
    return '—';
  }
  return min === max ? min : `${min} - ${max}`;
}

export function Products() {
  const { getToken, hasRole } = useAuth();
  const navigate = useNavigate();
  const [region, setRegion] = useState<string>(regions[0]?.code ?? 'au');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<ProductDataFile<RawRow>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [syncState, setSyncState] = useState<'idle' | 'triggering' | 'triggered'>('idle');

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const token = await getToken();
      setData(await getProducts(token, region, productsFeed.name));
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setLoading(false);
    }
  }, [getToken, region]);

  useEffect(() => {
    void load();
  }, [load]);

  const sync = async () => {
    setSyncState('triggering');
    setError(undefined);
    try {
      const token = await getToken();
      await triggerSync(token);
      setSyncState('triggered');
    } catch (cause) {
      setError((cause as Error).message);
      setSyncState('idle');
    }
  };

  const currency = regions.find((candidate) => candidate.code === region)?.currency;

  /** Rows matching the search, bucketed by door type in config order. */
  const byType = useMemo(() => {
    const buckets = new Map<DoorTypeId, RawRow[]>(doorTypeIds.map((id) => [id, []]));
    for (const row of data?.rows ?? []) {
      const type = parseDoorType(row.doorType);
      if (type && matchesSearch(row, search)) {
        buckets.get(type)?.push(row);
      }
    }
    return buckets;
  }, [data, search]);

  const detailPath = (row: RawRow): string | undefined => {
    const inventoryID = text(row, 'inventoryID');
    return inventoryID ? `/products/${region}/${encodeURIComponent(inventoryID)}` : undefined;
  };

  const formatPrice = (row: RawRow): string => {
    const raw = text(row, 'retailPrice');
    const value = Number(raw);
    return raw && Number.isFinite(value) && currency ? formatCurrency(value, currency) : raw || '—';
  };

  const renderTable = (rows: RawRow[]) => {
    if (rows.length === 0) {
      return (
        <Flex justify="center" py={12} data-name="products-empty">
          <Text color="fg.muted">No products found.</Text>
        </Flex>
      );
    }
    return (
      <Table.Root size="sm" data-name="products-table">
        <Table.Header>
          <Table.Row bg="gray.50">
            <Table.ColumnHeader>Inventory ID</Table.ColumnHeader>
            <Table.ColumnHeader>Description</Table.ColumnHeader>
            <Table.ColumnHeader>Bunnings #</Table.ColumnHeader>
            <Table.ColumnHeader>Status</Table.ColumnHeader>
            <Table.ColumnHeader>Frame</Table.ColumnHeader>
            <Table.ColumnHeader>Width (mm)</Table.ColumnHeader>
            <Table.ColumnHeader>Height (mm)</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Price</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row, index) => {
            const to = detailPath(row);
            const status = text(row, 'itemStatus');
            return (
              <Table.Row
                key={text(row, 'inventoryID') || index}
                data-name="product-row"
                cursor={to ? 'pointer' : undefined}
                _hover={to ? { bg: 'gray.50' } : undefined}
                onClick={to ? () => navigate(to) : undefined}
              >
                <Table.Cell whiteSpace="nowrap">
                  {to ? (
                    <RouterLink
                      to={to}
                      data-name="product-link"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Text as="span" color="primary.fg" fontWeight="medium">
                        {text(row, 'inventoryID')}
                      </Text>
                    </RouterLink>
                  ) : (
                    text(row, 'inventoryID')
                  )}
                </Table.Cell>
                <Table.Cell>{text(row, 'description')}</Table.Cell>
                <Table.Cell whiteSpace="nowrap">
                  {text(row, 'bunningsItemNumber') || '—'}
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    colorPalette={status === 'Active' ? 'green' : 'gray'}
                    data-name="product-status"
                  >
                    {status || 'Unknown'}
                  </Badge>
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap">{text(row, 'frameColour') || '—'}</Table.Cell>
                <Table.Cell whiteSpace="nowrap">
                  {formatBand(row, 'minDoorWidth', 'maxDoorWidth')}
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap">
                  {formatBand(row, 'minDoorHeight', 'maxDoorHeight')}
                </Table.Cell>
                <Table.Cell textAlign="right" whiteSpace="nowrap" fontWeight="medium">
                  {formatPrice(row)}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    );
  };

  return (
    <Stack gap={5} data-name="products-page">
      <Flex align="center" gap={3} wrap="wrap">
        <Heading size="lg">Products</Heading>
        <Flex gap={2} ml="auto" align="center">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
            data-name="refresh-products"
          >
            Refresh
          </Button>
          {hasRole('Admin') && (
            <Button
              size="sm"
              colorPalette="brand"
              onClick={sync}
              loading={syncState === 'triggering'}
              disabled={loading}
              data-name="trigger-sync"
            >
              Sync from Acumatica
            </Button>
          )}
        </Flex>
      </Flex>

      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <Flex gap={4} wrap="wrap" align="center">
          <Box flex="1" minW="240px">
            <Input
              size="sm"
              placeholder="Search by code, description, Bunnings # or colour…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              data-name="product-search"
            />
          </Box>
          <NativeSelect.Root size="sm" width="160px" data-name="select-region">
            <NativeSelect.Field value={region} onChange={(event) => setRegion(event.target.value)}>
              {regions.map((candidate) => (
                <option key={candidate.code} value={candidate.code}>
                  {candidate.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Text fontSize="sm" color="fg.muted" data-name="feed-meta">
            Last synced: {data ? new Date(data.syncedAt).toLocaleString() : '—'}
          </Text>
        </Flex>
      </Box>

      {syncState === 'triggered' && (
        <Box
          bg="green.50"
          borderWidth="1px"
          borderColor="green.200"
          borderRadius="md"
          p={4}
          data-name="sync-confirmation"
        >
          <Text color="green.700" fontSize="sm">
            Sync running in the background — use Refresh in a minute to see updated data.
          </Text>
        </Box>
      )}
      {error && (
        <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={4}>
          <Text color="red.600" data-name="products-error">
            {error}
          </Text>
        </Box>
      )}

      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        {loading && !data ? (
          <Flex justify="center" py={12} data-name="products-loading">
            <Spinner size="xl" color="brand.500" />
          </Flex>
        ) : (
          <Tabs.Root defaultValue={tabTypes[0]} colorPalette="brand">
            <Tabs.List px={4} pt={2}>
              {tabTypes.map((type) => (
                <Tabs.Trigger key={type} value={type} data-name={`${type}-tab`}>
                  {getDoorCopy(type).title} ({byType.get(type)?.length ?? 0})
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {tabTypes.map((type) => (
              <Tabs.Content key={type} value={type} data-name={`${type}-panel`}>
                {renderTable(byType.get(type) ?? [])}
              </Tabs.Content>
            ))}
          </Tabs.Root>
        )}
      </Box>
    </Stack>
  );
}
